import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CampaignService } from 'src/app/private/services/campaign.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

// Interfaces
interface TrackingEvent {
  id: string;
  campaign_id: string;
  client_id: string;
  message_id: string;
  recipient_phone: string;
  template_id: string;
  event_type: string;
  status: string;
  event_count: number;
  event_at: string;
  raw_event_data: string;
  createdAt: string;
  updatedAt: string;
}

interface CampaignStatistics {
  total: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
}

interface CampaignAnalytics {
  tracking_data: TrackingEvent[];
  statistics: CampaignStatistics;
}

interface StatCard {
  label: string;
  value: number;
  percentage: number;
  icon: string;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
}

@Component({
  selector: 'app-whatsapp-campaign-analytics',
  templateUrl: './whatsapp-campaign-analytics.component.html',
  styleUrls: ['./whatsapp-campaign-analytics.component.scss']
})
export class WhatsappCampaignAnalyticsComponent implements OnInit {
  isLoading = true;
  campaignId: string = '';

  analyticsData: CampaignAnalytics | null = null;
  statistics: CampaignStatistics | null = null;
  trackingEvents: TrackingEvent[] = [];

  statCards: StatCard[] = [];

  selectedEventType: string = 'all';
  eventTypes: string[] = ['all', 'Send', 'Delivered', 'Read', 'Failed'];

  currentPage = 1;
  itemsPerPage = 10;
  totalPages = 1;

  timelineChartInstance: any = null;
  funnelChartInstance: any = null;
  donutChartInstance: any = null;

  viewMode: 'table' | 'graph' = 'table';

  constructor(
    private campaignService: CampaignService,
    private localStorageService: LocalStorageService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.campaignId = this.route.snapshot.params['id'];
    this.getCampaignTracking();
  }

  getCampaignTracking() {
    this.isLoading = true;
    const data = {
      hospital_id: this.localStorageService.getHospitalId(),
      campaign_id: this.campaignId
    };

    this.campaignService.getWhatsappCampaignTracking(data).subscribe({
      next: (res: any) => {
        this.analyticsData = res;
        this.statistics = res.statistics;
        this.trackingEvents = res.tracking_data || [];
        this.calculateStatCards();
        this.updatePagination();
        this.isLoading = false;
        setTimeout(() => { this.initAllCharts(); }, 100);
      },
      error: (err) => {
        console.error('Error fetching WhatsApp campaign analytics:', err);
        this.isLoading = false;
      }
    });
  }

  calculateStatCards() {
    if (!this.statistics) return;

    const total = this.statistics.total || 1;
    const sent  = this.statistics.sent  || 1;
    const delivered = this.statistics.delivered || 1;

    this.statCards = [
      {
        label: 'Total Sent',
        value: this.statistics.sent,
        percentage: (this.statistics.sent / total) * 100,
        icon: 'fas fa-paper-plane',
        color: '#25D366',
        trend: 'neutral'
      },
      {
        label: 'Delivered',
        value: this.statistics.delivered,
        percentage: (this.statistics.delivered / sent) * 100,
        icon: 'fas fa-check-circle',
        color: '#10b981',
        trend: 'up'
      },
      {
        label: 'Read',
        value: this.statistics.read,
        percentage: (this.statistics.read / delivered) * 100,
        icon: 'fas fa-envelope-open',
        color: '#3b82f6',
        trend: 'up'
      },
      {
        label: 'Failed',
        value: this.statistics.failed,
        percentage: (this.statistics.failed / sent) * 100,
        icon: 'fas fa-exclamation-triangle',
        color: '#ef4444',
        trend: 'down'
      }
    ];
  }

  initAllCharts() {
    if (!this.statistics) return;
    this.prepareTimelineData();
    this.initFunnelChart();
    this.initDonutChart();
  }

  initFunnelChart() {
    if (this.funnelChartInstance) this.funnelChartInstance.destroy();
    const canvas = document.getElementById('waFunnelChart') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    this.funnelChartInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Sent', 'Delivered', 'Read'],
        datasets: [{
          label: 'Conversion',
          data: [this.statistics?.sent || 0, this.statistics?.delivered || 0, this.statistics?.read || 0],
          backgroundColor: ['rgba(37,211,102,0.8)', 'rgba(16,185,129,0.8)', 'rgba(59,130,246,0.8)'],
          borderRadius: 4,
          barPercentage: 0.6
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        plugins: {
          legend: { display: false },
          title: { display: true, text: 'WhatsApp Campaign Funnel', padding: 20 }
        },
        scales: { x: { display: false }, y: { grid: { display: false } } }
      }
    });
  }

  initDonutChart() {
    if (this.donutChartInstance) this.donutChartInstance.destroy();
    const canvas = document.getElementById('waDonutChart') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    this.donutChartInstance = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ['Delivered', 'Failed'],
        datasets: [{
          data: [this.statistics?.delivered || 0, this.statistics?.failed || 0],
          backgroundColor: ['#10b981', '#ef4444'],
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        cutout: '70%',
        plugins: {
          legend: { position: 'right' },
          title: { display: true, text: 'Delivery Status', padding: 20 }
        }
      }
    });
  }

  prepareTimelineData() {
    if (!this.trackingEvents.length) return;
    if (this.timelineChartInstance) this.timelineChartInstance.destroy();

    const filtered = this.filteredEvents;
    const sortedEvents = [...filtered].sort((a, b) =>
      new Date(a.event_at).getTime() - new Date(b.event_at).getTime()
    );

    const isSameDay = sortedEvents.length > 1 &&
      new Date(sortedEvents[0].event_at).toDateString() === new Date(sortedEvents[sortedEvents.length - 1].event_at).toDateString();

    const timeLabels: string[] = [];
    const timeMap: { [key: string]: number } = {};

    const getTimeKey = (timestamp: string) => {
      const date = new Date(timestamp);
      if (isSameDay) return date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    sortedEvents.forEach(event => {
      const key = getTimeKey(event.event_at);
      if (!timeMap.hasOwnProperty(key)) { timeMap[key] = 0; timeLabels.push(key); }
    });

    let datasets: any[] = [];

    if (this.selectedEventType === 'all') {
      const metrics = ['Send', 'Delivered', 'Read', 'Failed'];
      const colors: { [key: string]: string } = {
        'Send': '#25D366', 'Delivered': '#10b981', 'Read': '#3b82f6', 'Failed': '#ef4444'
      };
      datasets = metrics.map(metric => ({
        label: metric,
        data: timeLabels.map(label =>
          this.trackingEvents.filter(e =>
            getTimeKey(e.event_at) === label && e.event_type.toLowerCase() === metric.toLowerCase()
          ).length
        ),
        borderColor: colors[metric],
        backgroundColor: colors[metric],
        borderWidth: 2,
        pointBackgroundColor: '#fff',
        pointBorderColor: colors[metric],
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: false,
        tension: 0.4
      }));
    } else {
      const colorMap: { [key: string]: { border: string, bg: string } } = {
        'send':      { border: '#25D366', bg: 'rgba(37,211,102,0.1)' },
        'delivered': { border: '#10b981', bg: 'rgba(16,185,129,0.1)' },
        'read':      { border: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
        'failed':    { border: '#ef4444', bg: 'rgba(239,68,68,0.1)' }
      };
      let borderColor = '#25D366', backgroundColor = 'rgba(37,211,102,0.1)';
      const colors = colorMap[this.selectedEventType.toLowerCase()];
      if (colors) { borderColor = colors.border; backgroundColor = colors.bg; }
      datasets = [{
        label: this.selectedEventType,
        data: timeLabels.map(label => filtered.filter(e => getTimeKey(e.event_at) === label).length),
        borderColor, backgroundColor,
        borderWidth: 2, pointBackgroundColor: '#fff', pointBorderColor: borderColor,
        pointRadius: 4, pointHoverRadius: 6, fill: true, tension: 0.4
      }];
    }

    this.initTimelineChart(timeLabels, datasets);
  }

  initTimelineChart(labels: string[], datasets: any[]) {
    const canvas = document.getElementById('waTimelineChart') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    this.timelineChartInstance = new Chart(ctx, {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: datasets.length > 1, position: 'top', labels: { usePointStyle: true, boxWidth: 8, padding: 20 } },
          tooltip: {
            backgroundColor: 'rgba(255,255,255,0.95)',
            titleColor: '#1f2937', bodyColor: '#4b5563',
            borderColor: '#e5e7eb', borderWidth: 1, padding: 12, boxPadding: 4,
            usePointStyle: true, titleFont: { size: 13, weight: 'bold' }
          }
        },
        scales: {
          y: { beginAtZero: true, grid: { color: '#f3f4f6' }, ticks: { stepSize: 1 } },
          x: { grid: { display: false } }
        }
      }
    });
  }

  setViewMode(mode: 'table' | 'graph') {
    this.viewMode = mode;
    if (mode === 'graph') setTimeout(() => { this.initAllCharts(); }, 0);
  }

  get filteredEvents(): TrackingEvent[] {
    if (this.selectedEventType === 'all') return this.trackingEvents;
    return this.trackingEvents.filter(event =>
      event.event_type.toLowerCase() === this.selectedEventType.toLowerCase()
    );
  }

  get paginatedEvents(): TrackingEvent[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return this.filteredEvents.slice(startIndex, startIndex + this.itemsPerPage);
  }

  updatePagination() { this.totalPages = Math.ceil(this.filteredEvents.length / this.itemsPerPage); }

  setEventFilter(eventType: string) {
    this.selectedEventType = eventType;
    this.currentPage = 1;
    this.updatePagination();
    this.prepareTimelineData();
  }

  nextPage()     { if (this.currentPage < this.totalPages) this.currentPage++; }
  previousPage() { if (this.currentPage > 1) this.currentPage--; }
  goToPage(page: number) { this.currentPage = page; }

  getStatusBadgeClass(status: string): string {
    const statusMap: { [key: string]: string } = {
      'sent': 'badge-info', 'delivered': 'badge-success', 'read': 'badge-primary', 'failed': 'badge-danger'
    };
    return statusMap[status.toLowerCase()] || 'badge-default';
  }

  getEventIcon(eventType: string): string {
    const iconMap: { [key: string]: string } = {
      'send': 'bi-send-fill', 'delivered': 'bi-check-circle-fill',
      'read': 'bi-check-all', 'failed': 'bi-exclamation-triangle-fill'
    };
    return iconMap[eventType.toLowerCase()] || 'bi-info-circle-fill';
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  }

  formatPercentage(value: number): string { return value.toFixed(1); }

  formatPhoneNumber(phone: string): string {
    if (!phone) return '';
    if (phone.length > 10) {
      return `+${phone.substring(0, 2)} ${phone.substring(2, 7)} ${phone.substring(7)}`;
    }
    return `+91 ${phone}`;
  }

  viewRawData(event: TrackingEvent) {
    try { alert(JSON.stringify(JSON.parse(event.raw_event_data), null, 2)); }
    catch (error) { console.error('Error parsing raw event data:', error); }
  }

  exportData() {
    const dataStr = JSON.stringify(this.analyticsData, null, 2);
    const url = URL.createObjectURL(new Blob([dataStr], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `whatsapp-campaign-${this.campaignId}-analytics.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  goBack()      { this.router.navigate(['main/broadcast-campaign']); }
  refreshData() { this.getCampaignTracking(); }
}
